<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { requiredIf, required } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'

const { t } = useI18n()
const { umsokn, onLoaded } = useUmsokn()

const props = defineProps<{
  disable?: boolean
}>()

onLoaded(() => {
  if (umsokn.value) {
    if (typeof umsokn.value.creditMoveAll === 'undefined') umsokn.value.creditMoveAll = false
    if (typeof umsokn.value.creditMovePartial === 'undefined') umsokn.value.creditMovePartial = false
    if (typeof umsokn.value.creditPartialAmount === 'undefined') umsokn.value.creditPartialAmount = ''
  }
})

// Mutually exclusive toggles
const creditMoveAll = computed<boolean>({
  get: () => !!umsokn.value?.creditMoveAll,
  set: (val: boolean) => {
    if (!umsokn.value) return
    umsokn.value.creditMoveAll = val
    if (val) {
      umsokn.value.creditMovePartial = false
      umsokn.value.creditPartialAmount = ''
    }
  },
})
const creditMovePartial = computed<boolean>({
  get: () => !!umsokn.value?.creditMovePartial,
  set: (val: boolean) => {
    if (!umsokn.value) return
    umsokn.value.creditMovePartial = val
    if (val) {
      umsokn.value.creditMoveAll = false
    } else {
      umsokn.value.creditPartialAmount = ''
    }
  },
})

// Rule: require amount if partial is chosen
const creditPartialRequired = requiredIf(() => !!umsokn.value?.creditMovePartial)

// Rule: at least one checkbox must be selected
function atLeastOneSelected() {
  const u = umsokn.value
  return !!(u?.creditMoveAll || u?.creditMovePartial)
}

const rules = {
  umsokn: {
    creditPartialAmount: { creditPartialRequired },
    anySelected: { required: atLeastOneSelected }
  },
}

const v$ = useVuelidate(rules, { umsokn })

defineExpose({
  async validate() {
    await v$.value.$validate()
    if (v$.value.umsokn.anySelected.$invalid) return false
    return !v$.value.$error
  },
})
</script>

<template>
  <div v-if="umsokn">
    <v-col>
      <v-row>
        <v-col>
          <p class="forms__header" style="font-size: 18px">
            {{
              `${t('umsokn_uppsogn_samning_step3_oska_eftir')} (${umsokn.currentCustodian || ''}) ${t('umsokn_uppsogn_samning_step3_flutt')}`
            }}
          </p>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="12">
          <v-checkbox
            v-model="creditMoveAll"
            :label="t('umsokn_uppsogn_samning_step3_flytja_allt')"
            :readonly="props.disable"
            density="comfortable"
            hide-details
            class="mb-2"
          />
          <v-checkbox
            v-model="creditMovePartial"
            :label="t('umsokn_uppsogn_samning_step3_flytja_akvedid')"
            :readonly="props.disable"
            density="comfortable"
            hide-details
          />
          <p
            v-if="v$.umsokn.anySelected.$dirty && v$.umsokn.anySelected.$invalid"
            class="error__text mt-1"
          >
            {{ t('umsokn_general_required_error') }}
          </p>
        </v-col>
      </v-row>

      <v-row v-if="umsokn.creditMovePartial">
        <v-col cols="12" md="8">
          <v-text-field
            v-model.trim="umsokn.creditPartialAmount"
            :label="t('umsokn_uppsogn_samning_step3_flytja_akvedid_upphaed')"
            :placeholder="t('umsokn_uppsogn_samning_step3_flytja_akvedid_upphaed')"
            :readonly="props.disable"
            variant="outlined"
            inputmode="numeric"
            pattern="[0-9]*"
            :error="v$.umsokn.creditPartialAmount.$error"
            :error-messages="[
              v$.umsokn.creditPartialAmount.$error
                ? t('umsokn_uppsogn_samning_step3_flytja_akvedid_upphaed_error')
                : ''
            ].filter(Boolean)"
            hide-details="auto"
          />
        </v-col>
      </v-row>
    </v-col>
  </div>
</template>

<style scoped lang="scss">
.forms__header {
  color: #194458;
  font-weight: 900;
  font-size: 28px;
  line-height: 1.25;
  margin: 0 0 12px;
}
.error__text {
  color: #b00020;
  font-size: 0.875rem;
}
</style>